import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Drawer, Button, Space, Spin, Alert, Empty, Typography, Divider } from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import ListItemsDataTable from '../crud/ListItemsDataTable.component';
import DynamicFormSP from '../crud/DynamicFormSP.component';
import ApiErrorAlert from '../common/ApiErrorAlert';
import {
    loadVaultSubStudyDto,
    loadVaultPatientDto,
    listVaultSubStudies,
    createVaultSubStudy,
    updateVaultSubStudy,
    deleteVaultSubStudy,
    getVaultSubStudyDetails,
    listVaultSubStudiesByIds,
    listVaultSubStudyPatients,
} from '../../actions/identifierVaultActions';
import { VAULT_SUB_STUDY_PATIENTS_RESET } from '../../constants/identifierVaultConstants';

// Enrolments / NhsLinks stay off the form — code + description only.
const FIELDS_TO_HIDE = ['Id', 'CreatedAt', 'VaultPatientIds', 'NhsLinks'];
const SUMMARY_PROPS = ['futureStudyCode', 'description', 'createdAt'];
const PATIENT_FIELDS_TO_HIDE = [
    'Id', 'StudyHash', 'Salt', 'OrganisationId', 'TenantPatientId',
    'SubStudyIds', 'Address', 'MiddleName', 'OptOutSource', 'OptOutRequestedAt',
    'CreatedAt', 'UpdatedAt',
];

const drawerWidth = () =>
    Math.min(1100, typeof window !== 'undefined' ? Math.floor(window.innerWidth * 0.9) : 1100);

/**
 * Large side drawer: list / create / edit / delete sub-studies.
 * View/Edit form shows enrolled VaultPatients via ListItemsDataTable.
 */
export default function VaultSubStudyManagementDrawer({ open, onClose }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isListView, setIsListView] = useState(true);
    const [selectedEntityId, setSelectedEntityId] = useState(null);
    const [listFilter, setListFilter] = useState({
        query: '',
        page: 1,
        limit: 10,
        filter: 'futureStudyCode',
    });
    const [patientFilter, setPatientFilter] = useState({
        query: '',
        page: 1,
        limit: 10,
        filter: 'patientIdentifier',
    });

    const {
        vaultSubStudies = [],
        loading: listLoading,
        error: listError,
        currentPage,
        totalPages,
        totalCount,
    } = useSelector((s) => s.vaultSubStudyList || {});
    const {
        vaultSubStudyDto: entityDtoConfig,
        loading: loadingDto,
        error: dtoError,
    } = useSelector((s) => s.vaultSubStudyDto || {});
    const { vaultSubStudy: detailsRecord, error: detailsError, loading: detailsLoading } =
        useSelector((s) => s.vaultSubStudyDetails) || {};
    const {
        vaultPatientDto: patientDtoConfig,
    } = useSelector((s) => s.vaultPatientDto || {});
    const {
        vaultPatients: enrolledPatients = [],
        loading: patientsLoading,
        error: patientsError,
        currentPage: patientsPage,
        totalPages: patientsTotalPages,
        totalCount: patientsTotalCount,
    } = useSelector((s) => s.vaultSubStudyPatients || {});

    useEffect(() => {
        if (!open) return;
        setIsListView(true);
        setSelectedEntityId(null);
        dispatch(loadVaultSubStudyDto());
        dispatch(loadVaultPatientDto());
        dispatch(listVaultSubStudies(listFilter));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh on open only
    }, [open, dispatch]);

    useEffect(() => {
        if (!selectedEntityId || isListView) {
            dispatch({ type: VAULT_SUB_STUDY_PATIENTS_RESET });
            return;
        }
        dispatch(listVaultSubStudyPatients(selectedEntityId, patientFilter));
    }, [dispatch, selectedEntityId, isListView, patientFilter]);

    const refreshList = () => dispatch(listVaultSubStudies(listFilter));

    const handleCreate = () => {
        setSelectedEntityId(null);
        setIsListView(false);
    };

    const handleEdit = (id) => {
        setSelectedEntityId(id);
        setPatientFilter({ query: '', page: 1, limit: 10, filter: 'patientIdentifier' });
        setIsListView(false);
    };

    const handleDelete = (id) => {
        dispatch(deleteVaultSubStudy(id)).then(() => refreshList());
    };

    const handleBack = () => {
        setSelectedEntityId(null);
        setIsListView(true);
        refreshList();
    };

    const handleFilterChange = (next) => {
        setListFilter(next);
        dispatch(listVaultSubStudies(next));
    };

    const handlePatientFilterChange = (next) => {
        setPatientFilter(next);
    };

    const headerActions = isListView ? (
        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('Create')} {t('Sub-study')}
        </Button>
    ) : (
        <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('Back to')} {t('List')}
        </Button>
    );

    const patientDtoForTable = patientDtoConfig
        ? Object.fromEntries(
            Object.entries(patientDtoConfig).filter(([key]) => !PATIENT_FIELDS_TO_HIDE.includes(key))
        )
        : null;

    return (
        <Drawer
            title={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <span>{t('Sub-studies')}</span>
                    {headerActions}
                </Space>
            }
            open={open}
            onClose={onClose}
            width={drawerWidth()}
            destroyOnClose
        >
            {isListView ? (
                loadingDto && !entityDtoConfig ? (
                    <div style={{ padding: 48, textAlign: 'center' }}>
                        <Spin />
                    </div>
                ) : dtoError ? (
                    <ApiErrorAlert error={dtoError} title={t('Could not load form')} />
                ) : !entityDtoConfig ? (
                    <Alert type="warning" showIcon message={t('Could not load form')} />
                ) : (
                    <>
                        <ApiErrorAlert error={listError} title={t('Error loading sub-studies')} />
                        <ListItemsDataTable
                            loading={listLoading}
                            dtoConfig={entityDtoConfig}
                            data={vaultSubStudies}
                            filter={listFilter}
                            currentPage={currentPage || listFilter.page || 1}
                            totalPages={totalPages || 1}
                            totalCount={totalCount || 0}
                            limit={listFilter.limit || 10}
                            onFilterChange={handleFilterChange}
                            handleDeleteRow={handleDelete}
                            handleEditForm={handleEdit}
                            getEntityDetails={getVaultSubStudyDetails}
                            detailsRecord={detailsRecord}
                            detailsLoading={detailsLoading}
                            detailsError={detailsError}
                            enableActionButtons
                            locale={
                                (totalCount || 0) === 0 && !listLoading && !listError
                                    ? {
                                          emptyText: (
                                              <Empty
                                                  description={t('No sub-studies yet. Use Create to add a future study code.')}
                                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                              />
                                          ),
                                      }
                                    : undefined
                            }
                        />
                    </>
                )
            ) : (
                <div className="entity-management-form-container" style={{ width: '100%' }}>
                    <DynamicFormSP
                        key={selectedEntityId ?? 'new'}
                        currentEntityId={selectedEntityId}
                        selectedItemIds={selectedEntityId ? [selectedEntityId] : []}
                        defaultEditMode={selectedEntityId ? 'Edit' : 'Create'}
                        primarySearchProp="futureStudyCode"
                        singularEntity="vaultSubStudy"
                        pluralEntity="vaultSubStudies"
                        constantPrefix="VAULT_SUB_STUDY"
                        summaryProps={SUMMARY_PROPS}
                        loadDto={loadVaultSubStudyDto}
                        createEntity={(data) =>
                            dispatch(createVaultSubStudy(data)).then(() => {
                                handleBack();
                            })
                        }
                        updateEntity={(id, data) =>
                            dispatch(updateVaultSubStudy(id, data)).then(() => refreshList())
                        }
                        deleteEntity={deleteVaultSubStudy}
                        getEntityDetails={getVaultSubStudyDetails}
                        listEntities={listVaultSubStudies}
                        listEntitiesByIds={listVaultSubStudiesByIds}
                        isParent
                        fieldsToHide={FIELDS_TO_HIDE}
                        enableCreate
                        enableSearchBar={false}
                        enableActionButtons
                    />

                    {selectedEntityId && (
                        <>
                            <Divider />
                            <Typography.Title level={5} style={{ marginTop: 0 }}>
                                {t('Participants')}
                                {typeof patientsTotalCount === 'number' ? ` (${patientsTotalCount})` : ''}
                            </Typography.Title>
                            <ApiErrorAlert error={patientsError} />
                            {patientDtoForTable ? (
                                <ListItemsDataTable
                                    loading={patientsLoading}
                                    dtoConfig={patientDtoForTable}
                                    data={enrolledPatients}
                                    filter={patientFilter}
                                    currentPage={patientsPage || patientFilter.page || 1}
                                    totalPages={patientsTotalPages || 1}
                                    totalCount={patientsTotalCount || 0}
                                    limit={patientFilter.limit || 10}
                                    onFilterChange={handlePatientFilterChange}
                                    handleEditForm={(id) => {
                                        onClose();
                                        navigate(`/identifier_vault/${id}`);
                                    }}
                                    locale={
                                        (patientsTotalCount || 0) === 0 && !patientsLoading
                                            ? {
                                                  emptyText: (
                                                      <Empty
                                                          description={t('No participants enrolled yet. Enrol from a participant page.')}
                                                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                      />
                                                  ),
                                              }
                                            : undefined
                                    }
                                />
                            ) : (
                                <Spin />
                            )}
                        </>
                    )}
                </div>
            )}
        </Drawer>
    );
}
